import { Chip, TableCell, TableRow } from '@heroui/react';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiGrid, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageHeader } from '../../../../components/PageHeader';
import {
  type LoadingTableColumn,
  LoadingTable,
} from '../../../../components/Table';
import { useWorkspaces } from '../../hooks';
import type { Workspace } from '../../types';
import { locationLabel } from '../../utils';
import { CreateWorkspaceModal } from '../CreateWorkspaceModal';

const columns: LoadingTableColumn[] = [
  { label: 'Workspace', width: '35%', className: 'pl-6' },
  { label: 'Distributors', width: '45%' },
  { label: 'Id', width: '20%', className: 'hidden lg:table-cell pr-6' },
];

export const WorkspaceList: React.FC = () => {
  const navigate = useNavigate();
  const { workspaces, isLoading, error, refetch } = useWorkspaces();
  const [isCreateOpen, setCreateOpen] = useState(false);

  if (error) {
    return (
      <div className="p-6">
        <ErrorCard
          title="Could not load workspaces"
          message={error.message}
          onRetry={refetch}
        />
      </div>
    );
  }

  const renderRow = (workspace: Workspace) => (
    <TableRow
      key={workspace.id}
      className="cursor-pointer hover:bg-content2"
      onClick={() =>
        navigate(`/workspaces/${encodeURIComponent(workspace.id)}`)
      }
    >
      <TableCell className="pl-6 font-medium text-foreground">
        {workspace.name}
      </TableCell>
      <TableCell className="text-default-500">
        {workspace.roots.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {workspace.roots.map((root) => (
              <Chip key={root.id} size="sm" variant="flat" color="secondary">
                {locationLabel(root.name, root.id)}
              </Chip>
            ))}
          </div>
        ) : (
          // A workspace with no root is a data fault worth seeing, not hiding.
          <span className="text-warning">No distributor</span>
        )}
      </TableCell>
      <TableCell className="hidden lg:table-cell pr-6 font-mono text-tiny text-default-400">
        {workspace.id}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Workspaces - Choco Admin</title>
      </Helmet>
      <PageHeader
        title="Workspaces"
        description="Each workspace covers one distributor and everything under it."
        actions={[
          {
            label: 'Create workspace',
            icon: <FiPlus />,
            color: 'primary',
            onClick: () => setCreateOpen(true),
          },
        ]}
      />
      <LoadingTable<Workspace>
        columns={columns}
        ariaLabel="Workspaces"
        isLoading={isLoading}
        isEmpty={!isLoading && workspaces.length === 0}
        emptyState={{
          icon: FiGrid,
          title: 'No workspaces',
          description: 'Create one to start managing a distributor’s team.',
        }}
        data={workspaces}
        renderRow={renderRow}
      />
      <CreateWorkspaceModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
      />
    </div>
  );
};
